import { useEffect, useState } from 'react';

import { buildInfo } from '../../build-info.js';
import { CopyIcon } from '../../components/icons/CopyIcon.js';
import { ExternalLinkIcon } from '../../components/icons/ExternalLinkIcon.js';
import { MailIcon } from '../../components/icons/MailIcon.js';
import { NoteIcon } from '../../components/icons/NoteIcon.js';
import { buildReportLink } from './report-url.js';

/**
 * The three subordinate rows at the foot of the Help & FAQ page: ideas and requests, the
 * email fallback, and the build-info line.
 *
 * PROTOTYPE OF RECORD: `design_handoff_help_faq/` — `screenshots/05-ideas-email-buildinfo.png`,
 * README sections 4 and 5.
 *
 * WHY THEY ARE ONE COMPONENT AND NOT THREE SECTIONS. Every one of them is deliberately
 * *quieter* than what sits above it, and that is the design decision they share. The two
 * doors carry a privacy consequence and get cards with coloured top borders; these three
 * carry a suggestion, a workaround, and a diagnostic string. So none of them gets a
 * `<section>`, a `SectionHeader`, or the 20px section type scale — the ideas row's heading
 * is 16px on purpose, because it is not a peer of the sections above it. Splitting them
 * into three files would put that shared restraint in three places to be diluted one
 * component at a time.
 *
 * They still have to *exist*: the email fallback is the only route left for a report too
 * sensitive to publish, which is exactly what Door B's redaction warning tells the reporter
 * they have, and the build-info line is what lets a support conversation start with the
 * exact build rather than "the latest one".
 */
export function PageFooterRows() {
  return (
    <>
      <IdeasRow />
      <EmailFallbackRow />
      <BuildInfoRow />
    </>
  );
}

/**
 * The monitored alias for reports that cannot go to GitHub. Confirmed live during planning
 * (the plan's "Is the email alias real?" open question), so it ships as specified.
 *
 * Not exported: `PageFooterRows.test.tsx` asserts the literal address, which is what makes
 * a silent change to it fail a test instead of shipping a dead mailto.
 */
const SUPPORT_EMAIL = 'timesheets-help@austin.utexas.edu';

/**
 * Ideas and requests — a distinct intake, distinctly labelled (R12).
 *
 * Reuses `buildReportLink` with the idea template, which declares no body fields and
 * therefore carries no diagnostics. That is not "the defect call minus a few arguments":
 * `ReportLinkRequest` is a discriminated union, so the idea variant has no `diagnostics`
 * member to pass. Sending the defect params to a form that does not declare those ids is
 * behaviour GitHub documents neither way (silent ignore, or 404), and the union is what
 * makes the mistake unrepresentable here rather than merely discouraged.
 */
function IdeasRow() {
  const report = buildReportLink({ template: 'idea' });

  return (
    <div className="help-ideas">
      {/* The prototype's message glyph; `NoteIcon`'s path is identical to it. Decorative,
          so aria-hidden by the icon convention — which is why the new-tab affordance below
          is stated in text rather than left to the external-link glyph. */}
      <NoteIcon />
      <div>
        {/* An h2, as in the prototype, but at 16px — see this file's header on why it is
            not a `SectionHeader`. It follows the doors' h3s, so the outline steps back up
            to level 2 without skipping a level. */}
        <h2>Ideas and requests</h2>
        <p>
          Tell us what would make this easier — a shortcut you keep wanting, a report you keep
          rebuilding by hand.
        </p>
      </div>
      <a
        className="btn btn--secondary"
        href={report.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        Share an idea <ExternalLinkIcon />
        {/* The visible affordance is the glyph; this is the same fact for a reader who
            cannot see it. Door B states "Opens GitHub in a new tab" in visible copy
            because it also has a receipt to introduce; this row has no receipt to hang
            that sentence on, and a visible sentence here would give the quietest row on
            the page more chrome than the doors. */}
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
    </div>
  );
}

/**
 * The email fallback.
 *
 * Placed BELOW both GitHub buttons and styled down — no background, no border, 13px — so it
 * reads as the fallback rather than a fourth competing option. It is not optional, though:
 * Door B's redaction warning tells a reporter that anything sensitive does not belong in
 * the tracker, and without this row that instruction would end in a dead end.
 *
 * The mail link is Bluebonnet, not the prototype's Burnt Orange — divergence 3 in
 * `../help-faq.css`. It inherits that from the page-ground `.help-page a:not(.btn)` rule,
 * so this row declares no link colour of its own; one rule owns the page's links.
 */
function EmailFallbackRow() {
  return (
    <div className="help-fallback">
      <MailIcon />
      <p>
        Can&apos;t use GitHub, or the problem is too sensitive to post? Email{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we&apos;ll file it for you.
      </p>
    </div>
  );
}

/** How long the copy confirmation stays on screen, per the handoff. */
const COPY_CONFIRMATION_MS = 1800;

/**
 * What a press of Copy actually did. A named union rather than a bare boolean or two
 * inline literals: it selects both the announced sentence and the colour it is painted in,
 * so it is a domain value with two call sites, not display trivia.
 */
type CopyOutcome = 'copied' | 'failed';

/**
 * The announced sentence per outcome.
 *
 * `failed` exists because the prototype's confirmation is unconditional — it announces
 * "Copied" even on the insecure-origin path its own try/catch is there to absorb. A live
 * region that reports a success which did not happen is worse than no confirmation at all,
 * since the string beside it can still be selected by hand. Recorded as divergence 11.
 */
const COPY_MESSAGES: Readonly<Record<CopyOutcome, string>> = {
  copied: 'Copied',
  failed: "Couldn't copy — select the build line instead.",
};

/**
 * One press of Copy. `sequence` is what makes a repeat press distinguishable from the
 * press before it even when the outcome is identical — see {@link BuildInfoRow}.
 */
interface CopyAttempt {
  readonly sequence: number;
  readonly outcome: CopyOutcome;
}

/**
 * Writes `text` to the clipboard, reporting whether it landed.
 *
 * TWO FAILURE MODES, ONE ANSWER. `navigator.clipboard` is absent on an insecure origin
 * (and under jsdom), so the property access throws synchronously; a secure-origin write can
 * still reject when the document is not focused or the permission is denied. `await` inside
 * `try` catches both. Neither may escape — a page that throws out of a click handler for a
 * convenience button is worse than one that cannot copy — and neither may be reported as a
 * success, because the confirmation is the only feedback the press gives.
 */
async function writeToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * The build-info line and its Copy button.
 *
 * Both the displayed string and the copied one are `buildInfo.formatted` — the single field
 * that exists so what a reporter reads on screen cannot differ from what they paste into an
 * issue. Never re-derive it here from the parts.
 *
 * THE CONFIRMATION IS A LIVE REGION, AND THAT SHAPES EVERYTHING BELOW IT.
 *
 *  1. The `role="status"` span is rendered from first paint with empty text, never mounted
 *     on demand. A live region inserted into the DOM with its content already present is
 *     generally not announced — the region has to be there for the change to be a change.
 *     This is a deliberate departure from `ToastRegion`, which returns `null` when it has
 *     no toasts: right for a stack that owns its own corner of the viewport, wrong for an
 *     inline confirmation whose whole job is to announce one word.
 *
 *  2. A REPEAT PRESS RE-ANNOUNCES. A live region fires on DOM *change*, so writing
 *     `"Copied"` over `"Copied"` inside the 1.8s window mutates nothing and a screen-reader
 *     user hears silence from a press that felt identical to the first. So the click clears
 *     `announced` and bumps `attempt`; the effect keyed on `attempt` then sets the text
 *     again. The commit boundary between the two is what makes the second press a real
 *     mutation — empty, then "Copied" — and every activation therefore announces. The
 *     `sequence` counter is what makes `attempt` a new object even when the outcome
 *     repeats, so the effect re-runs; comparing outcomes alone would collapse exactly the
 *     repeat case this exists for.
 *
 *  3. THE TIMER IS OWNED BY THAT EFFECT, not by the click handler. React runs the cleanup
 *     before the next run and once more at unmount, so a second press cancels the first
 *     press's expiry (restarting the 1.8s from the press that just happened) and leaving
 *     `/help` cancels it outright. `/help` is a page people bounce off, so unmount with a
 *     timer pending is the ordinary path, not an edge case — see
 *     docs/solutions/ui-bugs/toast-timeout-not-cleared-on-unmount-state-update-warning-2026-05-10.md,
 *     whose fix stores ids in a ref and captures `ref.current` into a local before clearing
 *     them. There is no ref here on purpose: the effect that creates the timeout is the
 *     effect that clears it, so the id is a `const` in that closure and the hazard the
 *     learning warns about — reading a ref whose value has moved on by cleanup time —
 *     cannot arise.
 */
function BuildInfoRow() {
  const [attempt, setAttempt] = useState<CopyAttempt | null>(null);
  const [announced, setAnnounced] = useState<CopyOutcome | null>(null);

  useEffect(() => {
    // First paint: nothing has been copied, so the region stays present and empty.
    if (attempt === null) return;

    setAnnounced(attempt.outcome);
    const expiry = setTimeout(() => {
      setAnnounced(null);
    }, COPY_CONFIRMATION_MS);
    return () => {
      clearTimeout(expiry);
    };
  }, [attempt]);

  async function copyBuildInfo() {
    const outcome: CopyOutcome = (await writeToClipboard(buildInfo.formatted))
      ? 'copied'
      : 'failed';

    // Order matters, and so does the fact that these two land in one batch: the render
    // they cause commits an EMPTY region, and the effect above then fills it. Setting the
    // text here instead would skip that boundary and silence every repeat press.
    setAnnounced(null);
    setAttempt((previous) => ({ sequence: (previous?.sequence ?? 0) + 1, outcome }));
  }

  return (
    <div className="help-buildinfo">
      <span className="help-buildinfo-string">{buildInfo.formatted}</span>
      <button
        type="button"
        className="help-buildinfo-copy"
        // Names what is copied, and still contains the visible label, so WCAG 2.5.3 holds
        // and "click Copy" works for a voice-control user.
        aria-label="Copy build information"
        onClick={() => {
          void copyBuildInfo();
        }}
      >
        <CopyIcon /> Copy
      </button>
      <span
        className={
          announced === 'failed'
            ? 'help-buildinfo-status help-buildinfo-status--failed'
            : 'help-buildinfo-status'
        }
        role="status"
        // Explicit, as in `ToastRegion` — `role="status"` implies it, and some assistive
        // technology honours the attribute more reliably than the implicit mapping.
        // `aria-atomic` is deliberately NOT set to `false` the way the toast stack sets it:
        // that region holds several messages and wants only the new one read, while this one
        // is a single sentence that should be read whole.
        aria-live="polite"
      >
        {announced === null ? '' : COPY_MESSAGES[announced]}
      </span>
    </div>
  );
}
